import { useEffect, useState } from 'react';
import yaml from 'js-yaml';

/**
 * The metadata editors — team and application — are the same editor over
 * different corners of the declaration, so the parsing, the emptiness rule and
 * the one error they can show live here rather than twice.
 */
const useMetadataEditor = ({ metadata, onCommit }: { metadata: any; onCommit: (parsed: object) => void }) => {
    /* An empty object dumps as "{}", which is a thing on screen that the reader
       then has to delete before typing. Nothing is the truer rendering of
       nothing, and the commit below turns it back into `{}`. */
    const initial = metadata && typeof metadata === 'object' && Object.keys(metadata).length === 0
        ? ''
        : yaml.dump(metadata) ?? '';

    const [value, setValue] = useState<string>(initial);
    const [error, setError] = useState<string>('');

    // reopening on a different app, or after a save elsewhere, shows that app's
    // metadata rather than whatever was last typed
    useEffect(() => {
        setValue(initial);
        setError('');
        // `initial` is derived from `metadata`; keying on it directly would
        // reset the editor on every keystroke-driven re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata]);

    /** typing clears the error: it was about text that no longer exists */
    const change = (next: string) => {
        setValue(next);
        if (error) setError('');
    };

    const commit = () => {
        if (!value.trim()) {
            onCommit({});
            return;
        }
        let parsed: unknown;
        try {
            parsed = yaml.load(value);
        } catch (e) {
            setError('Invalid YAML. Please correct it before saving.');
            return;
        }
        /* YAML will happily parse `hello` or `- a` — both valid, neither
           metadata. Rejecting them here is what keeps the declaration's shape
           from depending on what someone typed. */
        if (parsed === null || parsed === undefined) {
            onCommit({});
            return;
        }
        if (typeof parsed !== 'object' || Array.isArray(parsed)) {
            setError('Metadata must be a set of key/value pairs.');
            return;
        }
        onCommit(parsed as object);
    };

    return { value, setValue: change, error, commit };
};

export default useMetadataEditor;
